import React from 'react'
import { SimpleLayout } from '../layouts/simple'
import { SpecialPanel } from '../layouts/components/panel'
import { ImageButton, Button, ButtonStyle } from '../components/simple/button'
import { AppName } from '../constants'
import { useAppContext } from '../providers/context'
import { IconAngleLeft, IconLanguage } from '../icons/library'
import { GoogleIcon } from '../icons/google'
import { Divider } from '../components/simple/divider'
import { useTranslation } from '../providers/translation'
import { TextInput } from '../components/form/textInput'
import { ArrowSelectDropdown } from '../components/simple/dropdown'
import { makeRequest, requestOrUndefined } from '../request'
import { useLanguages } from '../providers/languages'
import { useLocation } from 'react-router-dom'
import { Toasts } from '../providers/toast'
import { storage } from '../storage'

export const LoginPage: React.FC = () => {
  const { state } = useAppContext()
  const { tr, language, changeLanguage } = useTranslation()
  const languages = useLanguages()
  const languageOptions = React.useMemo(
    () => languages.getOptions((l) => l.googleTranslatable || l.code == 'en', true),
    [languages]
  )
  const location = useLocation()

  const [loadingLoginRequest, setLoadingLoginRequest] = React.useState<boolean>(false)
  const [loginRequestResponse, setLoginRequestResponse] = React.useState<string>('')
  const [loginAddress, setLoginAddress] = React.useState<string>('')

  const intendedURLParams = `/${location.search}${location.hash}`

  const sendLoginMessage = () => {
    if (!loginAddress || loadingLoginRequest) return

    setLoadingLoginRequest(true)
    setLoginRequestResponse('')
    setLoginAddress('')
    requestOrUndefined<string>('/sendLoginLink', {
      emailPhone: loginAddress,
      intendedURL:
        (storage.session.enabled ? storage.session.get('intendedPathForAfterLogin') : undefined) || intendedURLParams,
    }).then((response) => {
      setLoginRequestResponse(response || 'An error occurred')
      // clear non-crucial status
      if (status && !status.maintenance) setStatus(undefined)
      setLoadingLoginRequest(false)
    })
  }

  const [status, setStatus] = React.useState<
    { maintenance: boolean; header?: string; message?: string; statusPageLink?: string } | undefined
  >(undefined)
  React.useEffect(() => {
    makeRequest<NonNullable<typeof status>>('/frontend_api/getStatus')
      .then((response) =>
        setStatus({
          maintenance: response.maintenance,
          header: response.header,
          message: response.message,
          statusPageLink: response.statusPageLink,
        })
      )
      .catch(() => Toasts.showToast({ message: 'Error loading status', error: true }))
  }, [])

  const redirectToSSO = (path: string) => {
    // only save the current path if it is not already saved
    // if it is already saved, it means the user was trying to go to some other
    // path and then was redirected to the login page
    if (storage.session.enabled && !storage.session.get('intendedPathForAfterLogin'))
      storage.session.set('intendedPathForAfterLogin', intendedURLParams)
    window.location.href = path
  }

  return (
    <SimpleLayout
      footer={true}
      headerActions={
        state.appName == AppName.ReachMyTeach && (
          <Button link="https://info.reachmyteach.com/" className="ml-auto" buttonStyle={ButtonStyle.ghost}>
            <IconAngleLeft className="mr-2" />
            Home
          </Button>
        )
      }
    >
      <SpecialPanel className="items-stretch">
        <div className="mb-5 flex items-center">
          <div className="flex flex-grow">
            <div className="mx-auto text-3xl font-bold">{tr('Sign In')}</div>
          </div>
          {languageOptions.length > 0 && (
            <ArrowSelectDropdown
              options={languageOptions}
              onSelect={changeLanguage}
              className="mx-auto"
              buttonStyle={ButtonStyle.outlineBlue}
            >
              <IconLanguage size="2x" className="-my-5 -ml-2 mr-2" />
              {language?.name || 'Language'}
            </ArrowSelectDropdown>
          )}
        </div>
        <StatusMessage status={status} />
        {state.appName == AppName.KeyChat && (
          <ImageButton image={<img src="/images/faria.png" width={40} />} onClick={() => redirectToSSO('/auth/openID')}>
            Sign in with Faria
          </ImageButton>
        )}
        <ImageButton image={GoogleIcon} onClick={() => redirectToSSO('/auth/google')}>
          Sign In With Google
        </ImageButton>
        <ImageButton image={<img src="/images/microsoft.png"></img>} onClick={() => redirectToSSO('/auth/azure')}>
          Sign In With Microsoft
        </ImageButton>
        <Divider className="my-2">OR</Divider>
        <span>{tr('Enter your email or phone to instantly get a login link and access your account:')}</span>
        <div className="flex gap-2">
          <TextInput
            className="flex-grow"
            placeholder={tr('email or phone number')}
            onChange={setLoginAddress}
            onSubmit={sendLoginMessage}
            value={loginAddress}
            disabled={loadingLoginRequest}
          />
          <Button logo onClick={sendLoginMessage} disabled={loadingLoginRequest || loginAddress.trim() == ''}>
            {tr('Sign In')}
          </Button>
        </div>
        {loginRequestResponse && <div className="mt-2 rounded bg-warning p-2">{loginRequestResponse}</div>}
        <div className="mt-3">
          {tr('By using this service, you agree to our')} <a href="/terms-and-conditions">{tr('terms of service')}</a>{' '}
          {tr('and')} <a href="/privacy-policy">{tr('privacy policy')}</a>
        </div>
      </SpecialPanel>
    </SimpleLayout>
  )
}

type Status = { maintenance: boolean; header?: string; message?: string; statusPageLink?: string }

const StatusMessage: React.FC<{ status?: Status }> = ({ status }) => {
  if (!status) return null
  if (!status.header && !status.message && !status.statusPageLink) return null
  return (
    <div
      className={`${status.maintenance ? 'bg-red-500 text-white' : 'bg-warning-500'} flex flex-col gap-1 rounded px-4 py-2`}
    >
      {status.header && <div className="text-lg font-bold">{status.header}</div>}
      {status.message && <div>{status.message}</div>}
      {status.statusPageLink && (
        <a href={status.statusPageLink} target="_blank">
          Status Page
        </a>
      )}
    </div>
  )
}
